import { ApolloError } from '@apollo/client/core';

import { FullInvoiceFragment, FullJobFragment, InvoiceBasicDetailsFragment, InvoiceWithArtifactsFragment, } from '../../generated/graphql.generated';
import { isAegisArtifact } from '../artifacts/artifact.util';
import { getApolloErrorMessage } from '../utilities/errors.util';
import { getJobCustomer } from '../utilities/job-customer.util';

export type InvoiceStatus = 'generating' | 'draft' | 'finalized' | 'void' | 'deleted';

export function getInvoiceStatus(invoice: FullInvoiceFragment): InvoiceStatus {

    const [ associatedArtifact ] = invoice.artifacts;

    if (invoice.deletedAt) {
        return 'deleted';
    }

    if (invoice.voidedAt) {
      return 'void';
    }

    if (invoice.sentAt || invoice.openedAt) {
      return 'finalized';
    }

    const generated = associatedArtifact.attributes?.includes('status::draft');

    if (!generated) {
      return 'generating';
    }

    return 'draft';
}

export function getEventInvoicedStatus(invoices: Partial<FullInvoiceFragment>[]): 'finalized' | 'draft' | undefined {
        if (invoices.some(isFinalizedInvoice)) return 'finalized';
        if (invoices.some(isDraftInvoice)) return 'draft';
        return undefined;
    }

export type InvoiceFinalizationStatusFields = Pick<InvoiceWithArtifactsFragment, 'deletedAt' | 'voidedAt' | 'openedAt' | 'sentAt'>;

export function isFinalizedInvoice<T extends InvoiceFinalizationStatusFields>(invoice: T) {

    if (invoice.deletedAt || invoice.voidedAt) {
        return false;
    }

    return Boolean(invoice.openedAt || invoice.sentAt);
}

export function isDraftInvoice<T extends InvoiceFinalizationStatusFields>(invoice: T) {
  if (invoice.voidedAt || invoice.deletedAt) {
    return false;
  }
  return !invoice.sentAt && !invoice.openedAt;
}

export function hasLiveInvoice(event: { invoices?: Pick<InvoiceWithArtifactsFragment, 'deletedAt' | 'voidedAt'>[] }) {
  return (event.invoices ?? []).some((i) => !i.deletedAt && !i.voidedAt);
}

export function canBeInvoiced(
  event: { status?: string; start?: number; invoices?: Pick<InvoiceWithArtifactsFragment, 'deletedAt' | 'voidedAt'>[] },
  now: number,
) {
  const isCompleted = event.status === 'completed';

  const shouldBeCompleted = event.status === 'booked' && event.start < now;

  return !hasLiveInvoice(event) && (isCompleted || shouldBeCompleted);
}

export function shouldRefreshInvoiceDocument<
  T extends InvoiceFinalizationStatusFields & Pick<InvoiceWithArtifactsFragment, 'artifacts'>,
>(invoice: T) {

  const [ artifact ] = invoice?.artifacts ?? [];

  if (!artifact?.attributes?.includes('outdated')) {
    return false;
  }

  // Requirement: PandaDoc only edits documents in draft status, and opening or
  // sending an invoice takes its document out of draft.
  return isAegisArtifact(artifact) || !isFinalizedInvoice(invoice);
}

export function outdatedInvoiceReason(refusal: ApolloError): string {
  return `${ getApolloErrorMessage(refusal) } Don't send this copy to the customer.`;
}

export function getInvoiceName(invoice: InvoiceBasicDetailsFragment, job: FullJobFragment, includeCustomerName?: boolean) {

    if (!invoice) {
      return 'None';
    }

    const fragments = [ job.code ];

    if (includeCustomerName) {
      fragments.push(getJobCustomer(job.users, true) as string);
    }

    fragments.push(`#${ invoice.invoiceNumber }`);

    return fragments.join(' ');
}
